import i18n from "./i18n.ts";
import {
	appendMobileReturnStateToUrl,
	createMobileReturnUrl,
	type MobileReturnState,
} from "./mobilePageContext.ts";
import {
	resolveMobileSection,
	type MobileSection,
} from "./mobileNavigation.ts";
import { activeRouteInfos } from "./routeInfos.ts";

// N2-2: shared containment for "look-up" cross-tab deep links — team-name links
// in tables/lists and player links that were previously bare `leagueUrl([...])`.
// Wrapping them here seeds the N1 back stack (`m_return`) so a jump into another
// tab (e.g. Standings → a team's roster) climbs back precisely instead of
// falling to the destination section root. The origin page is auto-detected from
// the current URL so a single helper collects every call site.

const getMobileScrollY = () =>
	typeof window !== "undefined" ? window.scrollY : undefined;

// Precompiled path pattern → pageID matchers, derived from the same route table
// the router uses. Kept local (a ~5-line regex builder) so this low-level helper
// never has to import the heavyweight view manager / router modules.
const routeMatchers = Object.entries(activeRouteInfos).map(([path, pageID]) => {
	const parts = path.replace(/(^\/+|\/+$)/g, "").split("/");
	let source = "^";
	for (const part of parts) {
		source += part.startsWith(":") ? "/([^/]+?)" : `/${part}`;
	}
	source += "$";
	return { pageID, regex: new RegExp(source) };
});

export const resolveMobilePageIDFromPath = (pathname: string): unknown => {
	const normalized =
		pathname.length > 1 && pathname.endsWith("/")
			? pathname.slice(0, -1)
			: pathname;
	for (const matcher of routeMatchers) {
		if (matcher.regex.test(normalized)) {
			return matcher.pageID;
		}
	}
	return undefined;
};

// Resolves the page the user is currently on from `window.location`. Hrefs are
// built at render time, so the live path is the origin page for every link on
// it. Safe to call outside the browser (returns undefined during SSR).
export const getCurrentMobilePageID = (): unknown => {
	if (typeof window === "undefined") {
		return undefined;
	}
	const { pathname } = window.location;
	if (typeof pathname !== "string" || pathname.length === 0) {
		return undefined;
	}
	return resolveMobilePageIDFromPath(pathname);
};

// Source-page id → return-label descriptor (i18n key + English default). Only the
// pages that emit cross-tab look-up links need an entry; anything unlisted falls
// back to the destination section root label in the global back control.
const sourceLabelByPageID: Record<
	string,
	{ key: string; defaultValue: string }
> = {
	standings: { key: "standings.title", defaultValue: "Standings" },
	marketHub: { key: "marketHub.title", defaultValue: "Market" },
	// N2-1: source pages whose cross-tab action buttons (CTAs) now seed a
	// return so the global back control climbs back precisely.
	leagueDashboard: {
		key: "leagueDashboard.title",
		defaultValue: "League home",
	},
	teamFinances: { key: "teamFinances.title", defaultValue: "Team Finances" },
	roster: { key: "roster.title", defaultValue: "Roster" },
	teamStats: { key: "teamStats.title", defaultValue: "Team Stats" },
	playerStats: { key: "playerStats.title", defaultValue: "Player Stats" },
	playerRatings: { key: "playerRatings.title", defaultValue: "Player Ratings" },
	leaders: { key: "leaders.title", defaultValue: "League Leaders" },
	news: { key: "news.title", defaultValue: "News Feed" },
	history: { key: "history.title", defaultValue: "Season Summary" },
	freeAgents: { key: "freeAgents.title", defaultValue: "Free Agents" },
	draft: { key: "draft.title", defaultValue: "Draft" },
	draftHistory: { key: "draftHistory.title", defaultValue: "Draft History" },
	draftLottery: { key: "draftLottery.title", defaultValue: "Draft Lottery" },
	gameLog: { key: "gameLog.title", defaultValue: "Game Log" },
	liveGame: { key: "liveGame.title", defaultValue: "Live Game" },
	playoffs: { key: "playoffs.title", defaultValue: "Playoffs" },
	schedule: { key: "schedule.title", defaultValue: "Season Schedule" },
	dailySchedule: { key: "dailySchedule.title", defaultValue: "Matchday" },
};

const resolveSourceLabel = (pageID: unknown): string | undefined => {
	if (typeof pageID !== "string") {
		return undefined;
	}
	const descriptor = sourceLabelByPageID[pageID];
	if (!descriptor) {
		return undefined;
	}
	return i18n.t(descriptor.key, {
		ns: "ui",
		defaultValue: descriptor.defaultValue,
	});
};

// Wraps a bare destination url with a return seed so the back stack can climb
// back to the current page across tabs. `pageID`/`sourceLabel` are auto-detected
// but can be overridden by callers that already know their identity.
export const createMobileCrossTabHref = (
	destUrl: string,
	options?: { pageID?: unknown; sourceLabel?: string },
): string => {
	const pageID = options?.pageID ?? getCurrentMobilePageID();
	const sourceLabel = options?.sourceLabel ?? resolveSourceLabel(pageID);
	return createMobileReturnUrl(destUrl, {
		captureCurrentReturnUrl: true,
		pageID,
		scrollY: getMobileScrollY(),
		// `sourcePage` doubles as the signal that keeps the origin tab highlighted
		// (preserveSourceTab) while the look-up page is open.
		...(typeof pageID === "string" ? { sourcePage: pageID } : {}),
		...(sourceLabel ? { returnLabel: sourceLabel, sourceLabel } : {}),
	});
};

// Workspaces use the real destination view as a modal page. Unlike the
// cross-tab guard, this explicit presentation also applies to routes owned by
// the current tab (Market → Trade, Market → Free Agents).
export const createMobileRouteSheetHref = (
	destUrl: string,
	options?: { pageID?: unknown; sourceLabel?: string },
): string => {
	const pageID = options?.pageID ?? getCurrentMobilePageID();
	const sourceLabel = options?.sourceLabel ?? resolveSourceLabel(pageID);
	return createMobileReturnUrl(destUrl, {
		captureCurrentReturnUrl: true,
		pageID,
		presentation: "sheet",
		scrollY: getMobileScrollY(),
		...(typeof pageID === "string" ? { sourcePage: pageID } : {}),
		...(sourceLabel ? { returnLabel: sourceLabel, sourceLabel } : {}),
	});
};

// Links opened from a route-backed sheet stay in that sheet and retain the
// original workspace as the close target. Bottom-tab links are outside the
// content capture handler, so they remain deliberate full tab switches.
export const preserveMobileRouteSheetHref = ({
	destinationHref,
	state,
}: {
	destinationHref: string;
	state: MobileReturnState;
}): string => {
	if (typeof window === "undefined" || state.presentation !== "sheet") {
		return destinationHref;
	}

	let destination: URL;
	try {
		const currentHref =
			typeof window.location.href === "string" &&
			window.location.href.length > 0
				? window.location.href
				: `https://mobile-route-sheet.invalid${window.location.pathname || "/"}${window.location.search || ""}`;
		destination = new URL(destinationHref, currentHref);
	} catch {
		return destinationHref;
	}
	const currentOrigin = window.location.origin;
	const isRelativeAppHref =
		destinationHref.startsWith("/") && !destinationHref.startsWith("//");

	if (
		(!isRelativeAppHref &&
			typeof currentOrigin === "string" &&
			currentOrigin.length > 0 &&
			currentOrigin !== "null" &&
			destination.origin !== currentOrigin) ||
		!["http:", "https:"].includes(destination.protocol) ||
		destination.searchParams.get("m_view") === "sheet"
	) {
		return destinationHref;
	}

	// Destination links can carry their own back-stack seed (for example Free
	// Agents → Player). The workspace sheet owns close behavior, so replace only
	// those ownership fields while leaving destination-specific tab/filter state.
	for (const key of [
		"m_src",
		"m_src_page",
		"m_src_label",
		"m_view",
		"m_scroll",
		"m_return",
		"m_return_label",
	]) {
		destination.searchParams.delete(key);
	}

	return appendMobileReturnStateToUrl(
		`${destination.pathname}${destination.search}${destination.hash}`,
		state,
	);
};

const mobileMainSections = new Set<MobileSection>([
	"home",
	"team",
	"games",
	"market",
	"league",
]);

// Fail closed for links rendered anywhere in the league content area. A caller
// may forget to use createMobileCrossTabHref, but the click guard in Controller
// still turns a cross-domain lookup into the shared route sheet. Same-domain
// links and contextual player pages keep their normal navigation behavior.
export const rewriteMobileCrossTabHref = ({
	currentPageID,
	destinationHref,
	playerTid,
}: {
	currentPageID: unknown;
	destinationHref: string;
	playerTid?: number;
}): string => {
	if (typeof window === "undefined") {
		return destinationHref;
	}

	let destination: URL;
	try {
		const currentHref =
			typeof window.location.href === "string" &&
			window.location.href.length > 0
				? window.location.href
				: `https://mobile-cross-tab.invalid${window.location.pathname || "/"}${window.location.search || ""}`;
		destination = new URL(destinationHref, currentHref);
	} catch {
		return destinationHref;
	}

	const currentOrigin = window.location.origin;
	if (
		!(destinationHref.startsWith("/") && !destinationHref.startsWith("//")) &&
		typeof currentOrigin === "string" &&
		currentOrigin.length > 0 &&
		currentOrigin !== "null" &&
		destination.origin !== currentOrigin
	) {
		return destinationHref;
	}
	if (destination.searchParams.has("m_src_page")) {
		return destinationHref;
	}

	const sourceSection = resolveMobileSection(currentPageID, { playerTid });
	const destinationPageID = resolveMobilePageIDFromPath(destination.pathname);
	const destinationSection = resolveMobileSection(
		destinationPageID,
		destinationPageID === "player" &&
			mobileMainSections.has(sourceSection as MobileSection)
			? { originSection: sourceSection as MobileSection }
			: undefined,
	);

	if (
		!mobileMainSections.has(sourceSection as MobileSection) ||
		!mobileMainSections.has(destinationSection as MobileSection) ||
		sourceSection === destinationSection
	) {
		return destinationHref;
	}

	return createMobileCrossTabHref(
		`${destination.pathname}${destination.search}${destination.hash}`,
		{ pageID: currentPageID },
	);
};
